import { Box } from "@mui/material";
import { useState, type ReactNode } from "react";
import { v2 } from "../ui/v2Tokens";
import { SURFACE } from "../ui/primitives";
import { POINTS } from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";

/**
 * One macro card, shared by the calculator ("המלצת מקרונוטריאנטים") and the daily-values
 * menu, so both screens speak the same visual language: the macro's colour on the start
 * edge, the English name, the Hebrew name with an optional chip, the headline value, a
 * row of small stats, and a slider.
 *
 * The two screens drive it differently — the calculator slides grams per kg of bodyweight,
 * the daily-values menu slides grams or a share of the calorie target — so the value, the
 * stats and the slider's meaning are all passed in.
 */
type Props = {
  labelEn: string;
  labelHe: string;
  color: string;
  /** the headline figure and what it is measured in */
  value: number;
  unit: string;
  /** makes the headline figure editable instead of read-only */
  onValueChange?: (value: number) => void;
  /** what the headline figure is called — it is not always a gram target */
  valueLabel?: string;
  /** the small cells under the name */
  stats: Array<{ label: string; value: string }>;
  /** the slider — its own value, since it need not be the headline one */
  sliderValue: number;
  sliderLabel: string;
  min: number;
  max: number;
  step: number;
  onSliderChange: (value: number) => void;
  disabled?: boolean;
  /** the recommended figure, shown as a chip next to the name */
  recommendation?: string;
  /** small chip next to the name, e.g. "ממלא שארית" / "ידני" */
  badge?: string;
  badgeTone?: "neutral" | "amber";
  /** optional "override manually" toggle under the slider */
  overrideLabel?: string;
  onToggleOverride?: () => void;
  /** anything that belongs to this macro alone, below the slider */
  footer?: ReactNode;
};

/** One of the three cells under the macro name: a label and its value, side by side. */
const Stat = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "6px",
      padding: "5px 10px",
      borderRadius: "5px",
      background: SURFACE,
    }}
  >
    <Box sx={{ fontSize: "13px", color: v2.ink2 }}>{label}</Box>
    <Box sx={{ fontSize: "15px", fontWeight: 700, color: v2.ink }}>{value}</Box>
  </Box>
);

const MacroSliderRow = ({
  labelEn,
  labelHe,
  color,
  value,
  unit,
  onValueChange,
  valueLabel,
  stats,
  sliderValue,
  sliderLabel,
  min,
  max,
  step,
  onSliderChange,
  disabled,
  recommendation,
  badge,
  badgeTone = "neutral",
  overrideLabel,
  onToggleOverride,
  footer,
}: Props) => {
  /*
   * In points mode the headline is typed with a decimal ("1.5"), and the caller re-derives it from
   * the grams it stored — so "1." would come back as "1" and the point would be eaten. While the
   * field is being typed in it shows what was typed; on blur it goes back to the stored value.
   * Calories mode keeps the plain controlled input it always had.
   */
  const keepsDraft = useNutritionDisplayMode() === POINTS;
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <Box
      sx={{
        border: `1px solid ${v2.line}`,
        // the macro's colour reads down the start edge of the card, RTL-safe
        borderInlineStart: `3px solid ${color}`,
        borderRadius: "12px",
        background: v2.surface,
        overflow: "hidden",
        transition: "all .15s",
        // side by side, the cards fill the row so they all end on the same line
        width: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": { boxShadow: "0 2px 12px rgba(0,0,0,.08)" },
      }}
    >
      <Box
        sx={{
          padding: "10px 14px 11px",
          display: "flex",
          flexDirection: "column",
          gap: "9px",
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <Box>
            <Box
              sx={{
                fontSize: "11.5px",
                fontWeight: 700,
                letterSpacing: ".5px",
                textTransform: "uppercase",
                color,
                opacity: 0.75,
                marginBottom: "1px",
              }}
            >
              {labelEn}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
              <Box sx={{ fontSize: "14.5px", fontWeight: 600, color: v2.ink }}>{labelHe}</Box>
              {recommendation && (
                <Box
                  sx={{
                    fontSize: "11.5px",
                    fontWeight: 600,
                    padding: "1px 7px",
                    borderRadius: "20px",
                    color: v2.brand,
                    background: v2.brandWash,
                    border: "1px solid rgba(1,187,167,.2)",
                  }}
                >
                  {recommendation}
                </Box>
              )}
              {badge && (
                <Box
                  sx={{
                    fontSize: "11.5px",
                    fontWeight: 600,
                    padding: "1px 7px",
                    borderRadius: "20px",
                    color: badgeTone === "amber" ? v2.warn : v2.brand,
                    background: badgeTone === "amber" ? v2.warnWash : v2.brandWash,
                    border: `1px solid ${badgeTone === "amber" ? v2.warn : "rgba(1,187,167,.2)"}`,
                  }}
                >
                  {badge}
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "3px" }}>
            {onValueChange ? (
              <Box
                component="input"
                type="number"
                aria-label={valueLabel || `יעד ${labelHe}`}
                min={0}
                step={keepsDraft ? 0.1 : 1}
                value={keepsDraft && draft !== null ? draft : value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (keepsDraft) setDraft(e.target.value);
                  onValueChange(Number(e.target.value) || 0);
                }}
                onBlur={() => setDraft(null)}
                sx={{
                  /*
                   * Wide enough for FIVE characters at 24px/800 — "342.5" is an ordinary carb target
                   * once a calorie split lands on a half-gram. At 72px it did not fit, and because
                   * the text is end-aligned the overflow fell off the START: the trainer saw "42.5"
                   * and the leading digit was simply gone. A number you cannot read is one you
                   * cannot check, the same reason the quantity and serving fields were widened.
                   */
                  width: "104px",
                  flex: "none",
                  boxSizing: "border-box",
                  padding: "0 0 2px",
                  border: "none",
                  borderBottom: `1.5px solid ${v2.line}`,
                  background: "transparent",
                  fontSize: "24px",
                  fontWeight: 800,
                  letterSpacing: "-.5px",
                  fontFamily: "inherit",
                  color,
                  outline: "none",
                  textAlign: "end",
                  "&:focus": { borderBottomColor: color },
                }}
              />
            ) : (
              <Box sx={{ fontSize: "24px", fontWeight: 800, letterSpacing: "-.5px", lineHeight: 1, color }}>
                {value || "—"}
              </Box>
            )}
            <Box sx={{ fontSize: "12px", color: v2.ink3 }}>{unit}</Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "5px" }}>
          {stats.map((s) => (
            <Stat key={s.label} label={s.label} value={s.value} />
          ))}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Box sx={{ fontSize: "11.5px", color: v2.ink3, fontWeight: 500, minWidth: "16px" }}>{min}</Box>
          <Box
            component="input"
            type="range"
            aria-label={sliderLabel}
            min={min}
            max={max}
            step={step}
            value={sliderValue}
            disabled={disabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSliderChange(Number(e.target.value))}
            sx={{
              flex: 1,
              height: "3px",
              borderRadius: "2px",
              accentColor: color,
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.35 : 1,
            }}
          />
          <Box sx={{ fontSize: "11.5px", color: v2.ink3, fontWeight: 500, minWidth: "16px" }}>{max}</Box>
        </Box>

        {overrideLabel && onToggleOverride && (
          <Box sx={{ textAlign: "center", marginTop: "5px" }}>
            <Box
              component="button"
              type="button"
              onClick={onToggleOverride}
              sx={{
                padding: "3px 10px",
                borderRadius: "20px",
                fontSize: "12.5px",
                fontWeight: 600,
                fontFamily: "inherit",
                cursor: "pointer",
                color: disabled ? v2.ink2 : v2.warn,
                background: disabled ? "transparent" : v2.warnWash,
                border: `1px solid ${disabled ? v2.line : v2.warn}`,
                transition: "all .15s",
                "&:hover": { borderColor: v2.warn, color: v2.warn },
              }}
            >
              {overrideLabel}
            </Box>
          </Box>
        )}

        {footer}
      </Box>
    </Box>
  );
};

export default MacroSliderRow;
